/**
 * Digital storage units and transfer time, in exact arithmetic.
 *
 * The decimal (SI) prefixes — kilo = 1000 — and the binary (IEC) prefixes — kibi =
 * 1024 — are defined together in IEC 80000-13 (2008, revised 2025); the binary prefixes
 * first appeared in IEC 60027-2 in 1998. Every quantity here is a rational number of
 * bits held in BigInts, so 1 TB → TiB is not subject to floating-point drift and a
 * value such as 1 GiB can be shown as exactly 1,073,741,824 bytes.
 */

import {
  BIG_ONE,
  BIG_TEN,
  BIG_TWO,
  BIG_ZERO,
} from "@/lib/tool-kit/math/bigint";

const BYTE_BITS = BigInt(8);
const DECIMAL_MARK_PROBE = 1.1;
const SIGN_AND_DIGITS = /^([+-]?)(\d*)\.?(\d*)$/;
const MAX_INPUT_LENGTH = 40;

// ---------------------------------------------------------------------------
// Units
// ---------------------------------------------------------------------------

export type System = "base" | "si" | "iec";
export type Kind = "bit" | "byte";

export type UnitId =
  | "bit"
  | "B"
  | "kB"
  | "MB"
  | "GB"
  | "TB"
  | "PB"
  | "EB"
  | "ZB"
  | "YB"
  | "KiB"
  | "MiB"
  | "GiB"
  | "TiB"
  | "PiB"
  | "EiB"
  | "ZiB"
  | "YiB"
  | "kbit"
  | "Mbit"
  | "Gbit"
  | "Tbit"
  | "Pbit"
  | "Kibit"
  | "Mibit"
  | "Gibit"
  | "Tibit"
  | "Pibit";

export interface Unit {
  id: UnitId;
  kind: Kind;
  system: System;
  /** 1 for kilo/kibi … 8 for yotta/yobi; 0 for the plain bit and byte. */
  level: number;
  /** How many bits one unit holds. */
  bits: bigint;
}

const SI_BYTE: UnitId[] = ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
const IEC_BYTE: UnitId[] = [
  "KiB",
  "MiB",
  "GiB",
  "TiB",
  "PiB",
  "EiB",
  "ZiB",
  "YiB",
];
const SI_BIT: UnitId[] = ["kbit", "Mbit", "Gbit", "Tbit", "Pbit"];
const IEC_BIT: UnitId[] = ["Kibit", "Mibit", "Gibit", "Tibit", "Pibit"];

function pow(base: bigint, exponent: number): bigint {
  return base ** BigInt(exponent);
}

function ladder(
  ids: UnitId[],
  kind: Kind,
  system: System,
  perLevel: bigint
): Unit[] {
  const unitBits = kind === "byte" ? BYTE_BITS : BIG_ONE;
  return ids.map((id, index) => ({
    id,
    kind,
    system,
    level: index + 1,
    bits: unitBits * pow(perLevel, index + 1),
  }));
}

const THOUSAND = BigInt(1000);
const KIBI = BigInt(1024);

export const UNITS: readonly Unit[] = [
  { id: "bit", kind: "bit", system: "base", level: 0, bits: BIG_ONE },
  { id: "B", kind: "byte", system: "base", level: 0, bits: BYTE_BITS },
  ...ladder(SI_BYTE, "byte", "si", THOUSAND),
  ...ladder(IEC_BYTE, "byte", "iec", KIBI),
  ...ladder(SI_BIT, "bit", "si", THOUSAND),
  ...ladder(IEC_BIT, "bit", "iec", KIBI),
];

const BY_ID = new Map<UnitId, Unit>(UNITS.map((unit) => [unit.id, unit]));

export function unitOf(id: UnitId): Unit {
  const unit = BY_ID.get(id);
  if (!unit) {
    throw new Error(`unknown unit ${id}`);
  }
  return unit;
}

/** The same prefix in the other system: kB ↔ KiB, Gbit ↔ Gibit. Null for bit and byte. */
export function counterpart(id: UnitId): Unit | null {
  const unit = unitOf(id);
  if (unit.system === "base") {
    return null;
  }
  const other = unit.system === "si" ? "iec" : "si";
  return (
    UNITS.find(
      (candidate) =>
        candidate.kind === unit.kind &&
        candidate.system === other &&
        candidate.level === unit.level
    ) ?? null
  );
}

// ---------------------------------------------------------------------------
// Rational numbers
// ---------------------------------------------------------------------------

export interface Rational {
  n: bigint;
  /** Always positive. */
  d: bigint;
}

export function ratio(n: bigint, d: bigint = BIG_ONE): Rational {
  return { n, d };
}

export function multiply(a: Rational, b: Rational): Rational {
  return { n: a.n * b.n, d: a.d * b.d };
}

export function divide(a: Rational, b: Rational): Rational {
  return { n: a.n * b.d, d: a.d * b.n };
}

export function isZero(a: Rational): boolean {
  return a.n === BIG_ZERO;
}

export function toNumber(a: Rational): number {
  return Number(a.n) / Number(a.d);
}

export type DecimalParse =
  | { ok: true; value: Rational }
  | { ok: false; error: "empty" | "invalid" | "tooLong" };

/** Read a normalized decimal such as `1234.5` exactly. Negative values are not sizes. */
export function parseDecimal(text: string): DecimalParse {
  const trimmed = text.trim();
  if (trimmed === "") {
    return { ok: false, error: "empty" };
  }
  if (trimmed.length > MAX_INPUT_LENGTH) {
    return { ok: false, error: "tooLong" };
  }
  const match = SIGN_AND_DIGITS.exec(trimmed);
  if (!match || (match[2] === "" && match[3] === "") || match[1] === "-") {
    return { ok: false, error: "invalid" };
  }
  const fraction = match[3] ?? "";
  return {
    ok: true,
    value: ratio(
      BigInt(`${match[2] || "0"}${fraction}`),
      pow(BIG_TEN, fraction.length)
    ),
  };
}

// ---------------------------------------------------------------------------
// Conversion
// ---------------------------------------------------------------------------

/** Bits in `value` of `unit`. */
export function toBits(value: Rational, unit: Unit): Rational {
  return multiply(value, ratio(unit.bits));
}

/** `bits` expressed in `unit`. */
export function fromBits(bits: Rational, unit: Unit): Rational {
  return divide(bits, ratio(unit.bits));
}

export interface Gap {
  level: number;
  si: bigint;
  iec: bigint;
  /** How much smaller the decimal unit is than the binary one, as a percentage. */
  shortfall: number;
}

/** For each prefix, 1000^k against 1024^k: the gap that makes a "1 TB" drive read 931 GiB. */
export function gapTable(): Gap[] {
  const rows: Gap[] = [];
  for (let level = 1; level <= 8; level++) {
    const si = pow(THOUSAND, level);
    const iec = pow(KIBI, level);
    // Ten thousand times the ratio, in integers, to avoid float error.
    const scaled = Number((si * BigInt(1_000_000)) / iec) / 10_000;
    rows.push({ level, si, iec, shortfall: 100 - scaled });
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

export function decimalMark(locale: string): string {
  try {
    return (
      new Intl.NumberFormat(locale)
        .formatToParts(DECIMAL_MARK_PROBE)
        .find((part) => part.type === "decimal")?.value ?? "."
    );
  } catch {
    return ".";
  }
}

function groupInteger(value: bigint, locale: string): string {
  try {
    return value.toLocaleString(locale);
  } catch {
    return value.toString();
  }
}

/** n × 10^digits ÷ d, rounded half up. */
function scaledRound(r: Rational, digits: number): bigint {
  const scale = pow(BIG_TEN, digits);
  return (BIG_TWO * r.n * scale + r.d) / (BIG_TWO * r.d);
}

function trimZeros(fraction: string): string {
  let end = fraction.length;
  while (end > 0 && fraction[end - 1] === "0") {
    end -= 1;
  }
  return fraction.slice(0, end);
}

export interface FormattedRational {
  /** Locale-formatted text, or the mantissa of a scientific value. */
  text: string;
  /** Power of ten when the value is too small to write plainly. */
  exponent: number | null;
}

const PLAIN_LIMIT = 6;

/**
 * Write a non-negative rational with `significant` significant digits, in the locale's
 * digit grouping and decimal mark. Whole values are written in full; values below
 * 10^−6 come back as a mantissa and a power of ten.
 */
export function formatRational(
  r: Rational,
  locale: string,
  significant = 10
): FormattedRational {
  if (r.n === BIG_ZERO) {
    return { text: "0", exponent: null };
  }
  const mark = decimalMark(locale);
  const whole = r.n / r.d;
  if (whole > BIG_ZERO) {
    const places = Math.max(0, significant - whole.toString().length);
    const scaled = scaledRound(r, places);
    const scale = pow(BIG_TEN, places);
    const integer = scaled / scale;
    const fraction = trimZeros(
      (scaled % scale).toString().padStart(places, "0")
    );
    const head = groupInteger(integer, locale);
    return {
      text: fraction ? `${head}${mark}${fraction}` : head,
      exponent: null,
    };
  }
  // Below one: find k with value × 10^k ≥ 1.
  let k = 1;
  while (r.n * pow(BIG_TEN, k) < r.d) {
    k += 1;
  }
  if (k <= PLAIN_LIMIT) {
    const places = k - 1 + significant;
    const scaled = scaledRound(r, places);
    if (scaled === pow(BIG_TEN, places)) {
      return { text: "1", exponent: null };
    }
    const digits = trimZeros(scaled.toString().padStart(places, "0"));
    return { text: `0${mark}${digits}`, exponent: null };
  }
  const scaled = scaledRound(r, k + significant - 1).toString();
  const mantissa = trimZeros(scaled.slice(1, significant));
  const lead = scaled.slice(0, 1);
  return {
    text: mantissa ? `${lead}${mark}${mantissa}` : lead,
    // Rounding up to 10^significant carries into the next power of ten.
    exponent: scaled.length > significant ? 1 - k : -k,
  };
}

// ---------------------------------------------------------------------------
// Transfer time
// ---------------------------------------------------------------------------

export interface Duration {
  years: number;
  days: number;
  hours: number;
  minutes: number;
  /** Whole and fractional seconds. */
  seconds: number;
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86_400;
/** A Julian year, 365.25 days — the astronomers' convention. */
const SECONDS_PER_YEAR = 31_557_600;

/** Break a number of seconds into years, days, hours, minutes and seconds. */
export function splitDuration(totalSeconds: number): Duration {
  let rest = totalSeconds;
  const years = Math.floor(rest / SECONDS_PER_YEAR);
  rest -= years * SECONDS_PER_YEAR;
  const days = Math.floor(rest / SECONDS_PER_DAY);
  rest -= days * SECONDS_PER_DAY;
  const hours = Math.floor(rest / SECONDS_PER_HOUR);
  rest -= hours * SECONDS_PER_HOUR;
  const minutes = Math.floor(rest / SECONDS_PER_MINUTE);
  rest -= minutes * SECONDS_PER_MINUTE;
  return { years, days, hours, minutes, seconds: rest };
}

/**
 * Seconds to move `size` at `rate` per second with a link efficiency between 0 and 100
 * percent. Returns null when the effective rate is zero.
 */
export function transferSeconds(
  size: Rational,
  rate: Rational,
  efficiencyPercent: Rational
): Rational | null {
  const effective = multiply(rate, efficiencyPercent);
  if (isZero(effective)) {
    return null;
  }
  return multiply(divide(size, effective), ratio(BigInt(100)));
}
